// Tiny SVG line chart for the admin engagement page. Plain React, no
// deps — recharts couldn't fly under CRA 4 + Node 14 + a hot
// dev-server, and a 60-line custom component is plenty for one-line
// time-series tiles.
//
// Usage:
//   <MiniLineChart data={[{date, count}, ...]} color="#d97447" />

import React from 'react';

const W = 480;       // virtual width — scales via viewBox
const H = 160;       // virtual height
const PAD_L = 30;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 24;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

export default function MiniLineChart({ data, color = '#d97447' }) {
    if (!data || data.length === 0) {
        return <div style={{ color: '#bbb', fontSize: 13, padding: 12 }}>No data</div>;
    }

    const max = Math.max(1, ...data.map((d) => d.count || 0));
    const stepX = data.length > 1 ? PLOT_W / (data.length - 1) : 0;

    const points = data.map((d, i) => {
        const x = PAD_L + i * stepX;
        const y = PAD_T + PLOT_H - ((d.count || 0) / max) * PLOT_H;
        return [x, y];
    });
    const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
    const area = `${line} L ${PAD_L + (data.length - 1) * stepX} ${PAD_T + PLOT_H} L ${PAD_L} ${PAD_T + PLOT_H} Z`;

    // Three Y gridlines: 0, max/2, max
    const gridYs = [0, max / 2, max];

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: 180, display: 'block' }}
        >
            {gridYs.map((v) => {
                const y = PAD_T + PLOT_H - (v / max) * PLOT_H;
                return (
                    <g key={v}>
                        <line
                            x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                            stroke="#eee" strokeWidth={1}
                        />
                        <text
                            x={PAD_L - 6} y={y + 3}
                            textAnchor="end" fontSize={10} fill="#aaa"
                        >
                            {Math.round(v)}
                        </text>
                    </g>
                );
            })}

            <path d={area} fill={color} fillOpacity={0.12} stroke="none" />
            <path d={line} fill="none" stroke={color} strokeWidth={2} />

            {/* Date labels at the ends */}
            <text x={PAD_L} y={H - 6} fontSize={10} fill="#aaa">
                {shortDate(data[0].date)}
            </text>
            <text x={W - PAD_R} y={H - 6} fontSize={10} fill="#aaa" textAnchor="end">
                {shortDate(data[data.length - 1].date)}
            </text>
        </svg>
    );
}

function shortDate(iso) {
    if (!iso) return '';
    return iso.slice(5); // "YYYY-MM-DD" → "MM-DD"
}
